import {
  Cliente,
  ClienteCreateDTO,
  ClienteUpdateDTO,
  ConfigCreditoVendedorDTO,
  CreditoAbonoDTO,
  CreditoCreateDTO,
  CreditoItem,
  MovimientoCredito,
  MovimientoCreateDTO,
  HistorialRow,
  ResumenCreditoLocal,
  CreditoBalanceDiario,
  CreditoConfigLocal,
  CreditoMovimientoGlobal,
  CreditoResumenGlobal,
} from '../types/clientes.types';
import { fetchAPI } from './apiClient';

/**
 * Mapea respuesta del backend (snake_case) a aliases camelCase
 * para compatibilidad con componentes existentes.
 */
function mapCliente(raw: any): Cliente {
  return {
    ...raw,
    // Asegurar que valores monetarios sean números
    saldo_pendiente: Number(raw.saldo_pendiente) || 0,
    limite_credito: Number(raw.limite_credito) || 0,
    // Aliases camelCase
    limiteCredito: Number(raw.limite_credito) || 0,
    saldoPendiente: Number(raw.saldo_pendiente) || 0,
    localId: raw.local_id,
    estado: raw.bloqueado_credito ? 'SUSPENDIDO' : (raw.activo ? 'ACTIVO' : 'INACTIVO'),
    fechaRegistro: raw.created_at ? new Date(raw.created_at) : new Date(),
    vendedor_id: raw.vendedor_id,
    creditos: (raw.creditos || []).map(mapCredito),
  };
}

function mapCredito(raw: any): CreditoItem {
  return {
    ...raw,
    monto_total: Number(raw.monto_total) || 0,
    saldo_actual: Number(raw.saldo_actual) || 0,
  };
}

function mapResumenCreditoLocal(raw: any): ResumenCreditoLocal {
  const configLocal = raw.config_local || {};
  return {
    local_id: raw.local_id,
    total_credito_local: Number(raw.total_credito_local) || 0,
    vendedores: (raw.vendedores || []).map((item: any) => ({
      ...item,
      saldo_total_activo: Number(item.saldo_total_activo) || 0,
      max_monto_total: Number(item.max_monto_total) || 0,
      limite_credito_diario: Number(item.limite_credito_diario) || 0,
    })),
    config_local: {
      cantidad_creditos_activos: Number(configLocal.cantidad_creditos_activos) || 0,
      saldo_total_activo: Number(configLocal.saldo_total_activo) || 0,
      max_creditos: Number(configLocal.max_creditos) || 0,
      max_monto_total: Number(configLocal.max_monto_total) || 0,
      puede_vender_credito: Boolean(configLocal.puede_vender_credito),
      limite_credito_diario: Number(configLocal.limite_credito_diario) || 0,
      puede_registrar_abonos: Boolean(configLocal.puede_registrar_abonos),
      estado: configLocal.estado === 'ACTIVO' ? 'ACTIVO' : 'INACTIVO',
    },
  };
}

function numberize<T extends Record<string, any>>(raw: T, keys: string[]): T {
  const copy: any = { ...raw };
  keys.forEach((key) => { copy[key] = Number(raw[key]) || 0; });
  return copy;
}

class CreditoService {
  // ─── CLIENTES ────────────────────────────

  async listarClientes(localId?: string): Promise<Cliente[]> {
    const params = new URLSearchParams();
    if (localId) params.append('local_id', localId);
    const raw = await fetchAPI<any[]>(`/clientes?${params.toString()}`);
    return raw.map(mapCliente);
  }

  async obtenerCliente(id: string): Promise<Cliente> {
    const raw = await fetchAPI<any>(`/clientes/${id}`);
    return mapCliente(raw);
  }

  async crearCliente(data: ClienteCreateDTO): Promise<Cliente> {
    const raw = await fetchAPI<any>('/clientes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return mapCliente(raw);
  }

  async actualizarCliente(id: string, data: ClienteUpdateDTO): Promise<Cliente> {
    const raw = await fetchAPI<any>(`/clientes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return mapCliente(raw);
  }

  async toggleBloqueo(id: string, bloqueadoActual: boolean): Promise<Cliente> {
    return this.actualizarCliente(id, { bloqueado_credito: !bloqueadoActual });
  }

  async actualizarLimite(id: string, limite: number): Promise<Cliente> {
    return this.actualizarCliente(id, { limite_credito: limite });
  }

  async desactivarCliente(id: string): Promise<Cliente> {
    return this.actualizarCliente(id, { activo: false });
  }

  // ─── HISTORIAL ───────────────────────────

  async obtenerHistorial(clienteId: string): Promise<HistorialRow[]> {
    // Backend devuelve movimientos dentro de GET /clientes/{id}
    const raw = await fetchAPI<any>(`/clientes/${clienteId}`);
    const movimientos: any[] = raw.movimientos || [];
    // Ordenar cronológicamente y calcular saldo acumulado
    let saldoAcumulado = 0;
    return movimientos
      .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .map((m: any) => {
        const monto = Number(m.monto) || 0;
        // SUMA/POSITIVO = cargo (increases saldo/debt), RESTA/NEGATIVO = abono (decreases)
        const esCargo = m.efecto === 'SUMA' || m.efecto === 'POSITIVO';
        saldoAcumulado += esCargo ? monto : -monto;
        return {
          id: m.id,
          fecha: m.created_at,
          tipo: m.tipo,
          descripcion: m.descripcion || '',
          cargo: esCargo ? monto : null,
          abono: !esCargo ? monto : null,
          saldo_acumulado: saldoAcumulado,
          referencia_tipo: m.referencia_tipo || null,
        };
      });
  }

  // ─── MOVIMIENTOS ─────────────────────────

  async registrarMovimiento(
    clienteId: string,
    data: MovimientoCreateDTO
  ): Promise<MovimientoCredito> {
    return fetchAPI<MovimientoCredito>(`/clientes/${clienteId}/movimiento`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async crearCredito(data: CreditoCreateDTO): Promise<CreditoItem> {
    const raw = await fetchAPI<any>('/creditos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return mapCredito(raw);
  }

  async abonarCredito(creditoId: string, data: CreditoAbonoDTO): Promise<any> {
    return fetchAPI<any>(`/creditos/${creditoId}/abono`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async obtenerResumenLocal(): Promise<ResumenCreditoLocal> {
    const raw = await fetchAPI<any>('/creditos/resumen-local');
    return mapResumenCreditoLocal(raw);
  }

  async guardarConfigVendedor(vendedorId: string, data: ConfigCreditoVendedorDTO): Promise<any> {
    return fetchAPI<any>(`/vendedor/${vendedorId}/credito-config`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async obtenerResumenGlobal(): Promise<CreditoResumenGlobal> {
    const raw = await fetchAPI<any>('/creditos/resumen-global');
    return numberize(raw, [
      'clientes_total',
      'clientes_activos',
      'saldo_pendiente_total',
      'creditos_dados_hoy',
      'abonos_recibidos_hoy',
      'balance_caja_credito_hoy',
    ]) as CreditoResumenGlobal;
  }

  async listarClientesBanca(params: { local_id?: string; estado?: string } = {}): Promise<Cliente[]> {
    const qs = new URLSearchParams();
    if (params.local_id) qs.set('local_id', params.local_id);
    if (params.estado) qs.set('estado', params.estado);
    const raw = await fetchAPI<any[]>(`/creditos/clientes?${qs.toString()}`);
    return raw.map((item) => mapCliente({ ...item, saldo_pendiente: item.saldo_actual_credito ?? item.saldo_pendiente }));
  }

  async crearClienteBanca(data: ClienteCreateDTO & { usuario_login?: string; password?: string }): Promise<Cliente> {
    const raw = await fetchAPI<any>('/creditos/clientes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return mapCliente({ ...raw, saldo_pendiente: raw.saldo_actual_credito ?? raw.saldo_pendiente });
  }

  async actualizarClienteBanca(id: string, data: ClienteUpdateDTO & { usuario_login?: string; password?: string }): Promise<Cliente> {
    const raw = await fetchAPI<any>(`/creditos/clientes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return mapCliente({ ...raw, saldo_pendiente: raw.saldo_actual_credito ?? raw.saldo_pendiente });
  }

  async listarConfiguracion(): Promise<CreditoConfigLocal[]> {
    const raw = await fetchAPI<any[]>('/creditos/configuracion');
    return raw.map((item) => numberize(item, ['limite_credito_total', 'limite_credito_diario', 'max_creditos']) as CreditoConfigLocal);
  }

  async guardarConfigLocal(localId: string, data: Partial<CreditoConfigLocal>): Promise<any> {
    return fetchAPI<any>(`/creditos/configuracion/${localId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async obtenerBalanceDiario(fecha: string, localId?: string): Promise<CreditoBalanceDiario[]> {
    const qs = new URLSearchParams({ fecha });
    if (localId) qs.set('local_id', localId);
    const raw = await fetchAPI<any[]>(`/creditos/balance-diario?${qs.toString()}`);
    return raw.map((item) => numberize(item, [
      'saldo_anterior',
      'creditos_otorgados',
      'abonos_recibidos',
      'ajustes_positivos',
      'ajustes_negativos',
      'saldo_final',
      'balance_caja_credito',
    ]) as CreditoBalanceDiario);
  }

  async listarMovimientos(params: Record<string, string> = {}): Promise<CreditoMovimientoGlobal[]> {
    const qs = new URLSearchParams(params);
    const raw = await fetchAPI<any[]>(`/creditos/movimientos?${qs.toString()}`);
    return raw.map((item) => numberize(item, ['monto', 'saldo_anterior', 'saldo_nuevo']) as CreditoMovimientoGlobal);
  }

  async abonarCliente(clienteId: string, data: CreditoAbonoDTO): Promise<CreditoMovimientoGlobal> {
    const raw = await fetchAPI<any>(`/creditos/clientes/${clienteId}/abono`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return numberize(raw, ['monto', 'saldo_anterior', 'saldo_nuevo']) as CreditoMovimientoGlobal;
  }

  async pagarPremioCliente(clienteId: string, data: { monto: number; descripcion?: string }): Promise<{ saldo_anterior: number; saldo_nuevo: number; movimiento_id: string }> {
    const raw = await fetchAPI<any>(`/creditos/clientes/${clienteId}/pagar-premio`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return numberize(raw, ['saldo_anterior', 'saldo_nuevo']) as { saldo_anterior: number; saldo_nuevo: number; movimiento_id: string };
  }

  async eliminarCliente(clienteId: string): Promise<void> {
    await fetchAPI<void>(`/creditos/clientes/${clienteId}`, { method: 'DELETE' });
  }

  async ajustarCliente(clienteId: string, data: { monto: number; tipo: 'POSITIVO' | 'NEGATIVO'; motivo: string }): Promise<CreditoMovimientoGlobal> {
    const raw = await fetchAPI<any>(`/creditos/clientes/${clienteId}/ajuste`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return numberize(raw, ['monto', 'saldo_anterior', 'saldo_nuevo']) as CreditoMovimientoGlobal;
  }

  // ── Endpoints LOCAL / VENDEDOR ─────────────────────────────────────────────

  async listarClientesLocal(): Promise<Cliente[]> {
    const raw = await fetchAPI<any[]>('/creditos/local/clientes');
    return raw.map(mapCliente);
  }

  async listarMovimientosLocal(params: { cliente_id?: string; tipo?: string; fecha_desde?: string; fecha_hasta?: string } = {}): Promise<CreditoMovimientoGlobal[]> {
    const qs = new URLSearchParams();
    if (params.cliente_id) qs.set('cliente_id', params.cliente_id);
    if (params.tipo) qs.set('tipo', params.tipo);
    if (params.fecha_desde) qs.set('fecha_desde', params.fecha_desde);
    if (params.fecha_hasta) qs.set('fecha_hasta', params.fecha_hasta);
    const url = `/creditos/local/movimientos${qs.toString() ? '?' + qs.toString() : ''}`;
    const raw = await fetchAPI<any[]>(url);
    return raw.map((m) => numberize(m, ['monto', 'saldo_anterior', 'saldo_nuevo'])) as CreditoMovimientoGlobal[];
  }

  async abonarClienteLocal(clienteId: string, data: { monto: number; descripcion?: string }): Promise<CreditoMovimientoGlobal> {
    const raw = await fetchAPI<any>(`/creditos/local/clientes/${clienteId}/abono`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return numberize(raw, ['monto', 'saldo_anterior', 'saldo_nuevo']) as CreditoMovimientoGlobal;
  }

  async historialClienteBanca(clienteId: string): Promise<CreditoMovimientoGlobal[]> {
    const raw = await fetchAPI<any[]>(`/creditos/clientes/${clienteId}/movimientos`);
    return raw.map((m) => numberize(m, ['monto', 'saldo_anterior', 'saldo_nuevo'])) as CreditoMovimientoGlobal[];
  }

  /**
   * Transfiere un cliente de credito (y sus creditos abiertos) a otro vendedor
   * (local) de la misma banca. Solo BANCA.
   */
  async transferirCredito(
    clienteId: string,
    data: { vendedor_destino_id: string; motivo?: string },
  ): Promise<{
    cliente_id: string;
    vendedor_origen: string;
    vendedor_destino: string;
    creditos_transferidos: number;
    saldo: string;
    movimiento_id: string;
  }> {
    return fetchAPI(`/creditos/clientes/${clienteId}/transferir`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const creditoService = new CreditoService();
export default creditoService;
